import React from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { graphql } from 'react-apollo';
import { useOnboardingState } from '../../contexts/OnboardingContext';
import { currencySymbol } from '../../utils/formatters';
import Tabs from '../../../../ui/canon/Tabs';
import { exceedsRevenueCap, revenueCapMessage } from '../../utils/revenueCap';
import { getRevenueLineTerm, getRevenueLineTerminology } from '../Succession/revenueLineTerminology';

const MULTI_LINE_VALUATION_FEATURE = 'multi_line_valuation';

// Multi-line valuation feature flag mapping (matches Succession multi-line revenue model)
const LINE_FEATURES = {
  wealth: 'multi_line_valuation_wealth_management',
  life: 'multi_line_valuation_life_insurance',
  pc: 'multi_line_valuation_p_and_c',
  group: 'multi_line_valuation_group',
};

const LINE_LABELS = {
  wealth: 'Wealth',
  life: 'Life',
  pc: 'P&C',
  group: 'Group',
};

function normalizeStringArray(list) {
  if (!Array.isArray(list)) return [];
  return list.map(String);
}

function getCompanyFeatureTypesFromContext({ user }) {
  // Prefer `feature_types` (array of enabled flags). Fall back to wizard bootstrap data.
  const fromUser = normalizeStringArray(user?.company?.feature_types);
  if (fromUser.length > 0) return fromUser;
  const fromWizard = normalizeStringArray(window?.__WIZARD_DATA__?.companySettings?.feature_types);
  if (fromWizard.length > 0) return fromWizard;
  const fromWizardInitial = normalizeStringArray(window?.__INITIAL_WIZARD_DATA__?.companySettings?.feature_types);
  return fromWizardInitial;
}

function isMultiLineValuationEnabledFromTypes(featureTypes) {
  return featureTypes.includes(MULTI_LINE_VALUATION_FEATURE);
}

function enabledLinesFromTypes(featureTypes) {
  // When multi-line is enabled, show only enabled line flags; default to wealth if none.
  if (!isMultiLineValuationEnabledFromTypes(featureTypes)) return ['wealth'];
  const lines = Object.entries(LINE_FEATURES)
    .filter(([, featureName]) => featureTypes.includes(featureName))
    .map(([line]) => line);
  return lines.length > 0 ? lines : ['wealth'];
}

// Using fields that exist in the schema
const UPDATE_CURRENT_USER = gql`
  mutation updateCurrentUser(
    $number_of_clients: Int
    $fee_based_revenue: Float
    $trailer_revenue: Float
    $direct_revenue: Float
    $transactional_revenue: Float
    $recurring_revenue: Float
    $assets_under_management: Float
    $avg_client_age: Int
    $years_practicing: Int
    $annual_revenue: Float
    $li_first_year_commissions: Float
    $li_renewal_commissions: Float
    $li_override_commissions: Float
    $li_other_revenues: Float
    $li_policy_count: Int
    $pc_personal_lines_premium: Float
    $pc_personal_lines_commission_rate: Float
    $pc_personal_retention_rate: Float
    $pc_commercial_lines_premium: Float
    $pc_commercial_lines_commission_rate: Float
    $pc_commercial_retention_rate: Float
    $group_first_year_commissions: Float
    $group_renewal_commissions: Float
    $group_override_commissions: Float
    $group_other_revenues: Float
    $group_number_of_cases: Int
    $group_average_case_size: Float
    $group_case_retention_rate: Float
    $locale: String
  ) {
    updateCurrentUser(
      number_of_clients: $number_of_clients
      fee_based_revenue: $fee_based_revenue
      trailer_revenue: $trailer_revenue
      direct_revenue: $direct_revenue
      transactional_revenue: $transactional_revenue
      recurring_revenue: $recurring_revenue
      assets_under_management: $assets_under_management
      avg_client_age: $avg_client_age
      years_practicing: $years_practicing
      annual_revenue: $annual_revenue
      li_first_year_commissions: $li_first_year_commissions
      li_renewal_commissions: $li_renewal_commissions
      li_override_commissions: $li_override_commissions
      li_other_revenues: $li_other_revenues
      li_policy_count: $li_policy_count
      pc_personal_lines_premium: $pc_personal_lines_premium
      pc_personal_lines_commission_rate: $pc_personal_lines_commission_rate
      pc_personal_retention_rate: $pc_personal_retention_rate
      pc_commercial_lines_premium: $pc_commercial_lines_premium
      pc_commercial_lines_commission_rate: $pc_commercial_lines_commission_rate
      pc_commercial_retention_rate: $pc_commercial_retention_rate
      group_first_year_commissions: $group_first_year_commissions
      group_renewal_commissions: $group_renewal_commissions
      group_override_commissions: $group_override_commissions
      group_other_revenues: $group_other_revenues
      group_number_of_cases: $group_number_of_cases
      group_average_case_size: $group_average_case_size
      group_case_retention_rate: $group_case_retention_rate
      locale: $locale
    )
  }
`;

// Create a higher-order component to connect the class component to context
const withOnboardingContext = WrappedComponent => {
  return props => {
    const context = useOnboardingState() || {};
    const currentLanguage = context.currentLanguage || props.currentLanguage || 'en';
    console.log('ProductionInformationStep language from context:', currentLanguage);
    return <WrappedComponent {...props} currentLanguage={currentLanguage} onboardingContext={context} />;
  };
};

class ProductionInformationStep extends React.Component {
  constructor(props) {
    super(props);
    
    // Get initial data from props or wizard data
    const resumeData = window.__WIZARD_DATA__?.resumeData || {};
    
    // The default age should be 45 (middle age)
    const defaultAge = 45;
    
    // Use user.avg_client_age if it exists, even if it's 0
    // Only use defaultAge if user.avg_client_age is null or undefined
    const userAge = props.user?.avg_client_age;
    const resumeAge = resumeData?.average_client_age;
    const clientAge = userAge !== null && userAge !== undefined ? userAge : 
                      resumeAge !== null && resumeAge !== undefined ? resumeAge : defaultAge;
    
    this.state = {
      formData: {
        clientCount: props.user?.number_of_clients || resumeData?.number_of_clients || '',
        averageClientAge: clientAge, // Use determined client age from above
        aum: props.user?.assets_under_management || resumeData?.assets_under_management || 0,
        feeRevenue: props.user?.fee_based_revenue || resumeData?.fee_based_revenue || 0,
        trailerRevenue: props.user?.trailer_revenue || resumeData?.trailer_revenue || 0,
        directRevenue: props.user?.direct_revenue || resumeData?.direct_revenue || 0,
        commissionRevenue: props.user?.transactional_revenue || resumeData?.transactional_revenue || 0,
        premium: props.user?.premium || resumeData?.premium || 0,
        recurringRevenue: props.user?.recurring_revenue || resumeData?.recurring_revenue || 0,
        nonRecurringRevenue: props.user?.percent_non_recurring_revenue || resumeData?.non_recurring_revenue || 0,
        yearsPracticing: props.user?.years_practicing || resumeData?.years_practicing || '',
        annualRevenue: props.user?.annual_revenue || resumeData?.annual_revenue || 0,

        // Multi-line fields (no historical in onboarding)
        liFirstYearCommissions: props.user?.li_first_year_commissions || resumeData?.li_first_year_commissions || 0,
        liRenewalCommissions: props.user?.li_renewal_commissions || resumeData?.li_renewal_commissions || 0,
        liOverrideCommissions: props.user?.li_override_commissions || resumeData?.li_override_commissions || 0,
        liOtherRevenues: props.user?.li_other_revenues || resumeData?.li_other_revenues || 0,
        liPolicyCount: props.user?.li_policy_count || resumeData?.li_policy_count || 0,

        pcPersonalLinesPremium: props.user?.pc_personal_lines_premium || resumeData?.pc_personal_lines_premium || 0,
        pcPersonalLinesCommissionRate: ((props.user?.pc_personal_lines_commission_rate || resumeData?.pc_personal_lines_commission_rate || 0) * 100),
        pcPersonalRetentionRate: ((props.user?.pc_personal_retention_rate || resumeData?.pc_personal_retention_rate || 0) * 100),
        pcCommercialLinesPremium: props.user?.pc_commercial_lines_premium || resumeData?.pc_commercial_lines_premium || 0,
        pcCommercialLinesCommissionRate: ((props.user?.pc_commercial_lines_commission_rate || resumeData?.pc_commercial_lines_commission_rate || 0) * 100),
        pcCommercialRetentionRate: ((props.user?.pc_commercial_retention_rate || resumeData?.pc_commercial_retention_rate || 0) * 100),

        groupFirstYearCommissions: props.user?.group_first_year_commissions || resumeData?.group_first_year_commissions || 0,
        groupRenewalCommissions: props.user?.group_renewal_commissions || resumeData?.group_renewal_commissions || 0,
        groupOverrideCommissions: props.user?.group_override_commissions || resumeData?.group_override_commissions || 0,
        groupOtherRevenues: props.user?.group_other_revenues || resumeData?.group_other_revenues || 0,
        groupNumberOfCases: props.user?.group_number_of_cases || resumeData?.group_number_of_cases || 0,
        groupAverageCaseSize: props.user?.group_average_case_size || resumeData?.group_average_case_size || 0,
        groupCaseRetentionRate: ((props.user?.group_case_retention_rate || resumeData?.group_case_retention_rate || 0) * 100),
      },
      errors: {},
      isSubmitting: false,
      activeLine: (enabledLinesFromTypes(getCompanyFeatureTypesFromContext({ user: props.user }))[0] || 'wealth'),
    };
    
    // Bind methods
    this.handleChange = this.handleChange.bind(this);
    this.handleSliderChange = this.handleSliderChange.bind(this);
    this.handleCurrencyChange = this.handleCurrencyChange.bind(this);
    this.handleNumberChange = this.handleNumberChange.bind(this);
    this.validateForm = this.validateForm.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleBack = this.handleBack.bind(this);
  }

  getCompanyFeatureTypes() {
    return getCompanyFeatureTypesFromContext({ user: this.props.user });
  }

  isMultiLineValuationEnabled() {
    return isMultiLineValuationEnabledFromTypes(this.getCompanyFeatureTypes());
  }

  enabledLines() {
    return enabledLinesFromTypes(this.getCompanyFeatureTypes());
  }

  isLineFeatureEnabled(line) {
    const featureTypes = this.getCompanyFeatureTypes();
    const featureName = LINE_FEATURES[line];
    return !!featureName && featureTypes.includes(featureName);
  }

  maxRevenueInput() {
    return this.props.companyRevenueSettings?.company?.max_revenue_input;
  }

  // Continue waits for the cap so the client check never runs against an unknown limit.
  companyRevenueSettingsLoading() {
    const settings = this.props.companyRevenueSettings;
    return !!settings && settings.loading && !settings.company;
  }

  // Wealth labels follow the tenant's revenue-line terminology so onboarding matches
  // the profile Revenue Breakdown modal. French keeps the onboarding translations.
  wealthRevenueLabel(translationKey, termKey) {
    if (this.props.currentLanguage === 'fr') return this.getTranslatedText(translationKey);
    const raw = this.props.companyRevenueSettings?.company?.revenue_line_terminology;
    return getRevenueLineTerm(getRevenueLineTerminology(raw), 'wealth', termKey).label;
  }

  // Get company features to determine which fields to show
  getCompanySettings() {
    const { user } = this.props;
    const companyType = user?.company?.company_type ||
      window?.__WIZARD_DATA__?.companySettings?.company_type ||
      'other';

    // `companySettings.features` is a whitelist hash of booleans on the server
    // (show_county, enable_wealth_life_fields_in_onboarding, etc.) and does NOT
    // contain every company feature flag. Read the full list from
    // `feature_types` (array of enabled flag names) via the shared helper.
    const companyFeatures = getCompanyFeatureTypesFromContext({ user });

    const showWealthLifeFields = companyFeatures.includes('enable_wealth_life_fields_in_onboarding');
    const showPCFields = companyFeatures.includes('enable_pc_fields_in_onboarding');
    const showAUM = companyFeatures.includes('request_aum_on_sutd');

    return { showWealthLifeFields, showPCFields, showAUM };
  }

  // Get business type from user or wizard data
  getBusinessType() {
    return this.props.user?.company?.business_type || 
           window.__WIZARD_DATA__?.companySettings?.business_type || 
           'other';
  }

  // Age range markers
  ageMarkers = [
    { value: 25, label: '< 30' },
    { value: 40, label: '40' },
    { value: 55, label: '55' },
    { value: 70, label: '70' },
    { value: 85, label: '> 80' }
  ];

  // Update form data if user data changes
  componentDidUpdate(prevProps) {
    const { user } = this.props;
    
    if (!user || user === prevProps.user) return;
    
    const financialFields = [
      { prop: 'number_of_clients', state: 'clientCount' },
      { prop: 'avg_client_age', state: 'averageClientAge' },
      { prop: 'assets_under_management', state: 'aum' },
      { prop: 'fee_based_revenue', state: 'feeRevenue' },
      { prop: 'trailer_revenue', state: 'trailerRevenue' },
      { prop: 'direct_revenue', state: 'directRevenue' },
      { prop: 'transactional_revenue', state: 'commissionRevenue' },
      { prop: 'recurring_revenue', state: 'recurringRevenue' },
      { prop: 'percent_non_recurring_revenue', state: 'nonRecurringRevenue' },
      { prop: 'premium', state: 'premium' },
      { prop: 'years_practicing', state: 'yearsPracticing' },
      { prop: 'annual_revenue', state: 'annualRevenue' },

      // Multi-line fields (no historical)
      { prop: 'li_first_year_commissions', state: 'liFirstYearCommissions' },
      { prop: 'li_renewal_commissions', state: 'liRenewalCommissions' },
      { prop: 'li_override_commissions', state: 'liOverrideCommissions' },
      { prop: 'li_other_revenues', state: 'liOtherRevenues' },
      { prop: 'li_policy_count', state: 'liPolicyCount' },

      { prop: 'pc_personal_lines_premium', state: 'pcPersonalLinesPremium' },
      // Rates stored in DB as decimals; UI expects percent
      { prop: 'pc_personal_lines_commission_rate', state: 'pcPersonalLinesCommissionRate', transform: (v) => Number(v) * 100 },
      { prop: 'pc_personal_retention_rate', state: 'pcPersonalRetentionRate', transform: (v) => Number(v) * 100 },
      { prop: 'pc_commercial_lines_premium', state: 'pcCommercialLinesPremium' },
      { prop: 'pc_commercial_lines_commission_rate', state: 'pcCommercialLinesCommissionRate', transform: (v) => Number(v) * 100 },
      { prop: 'pc_commercial_retention_rate', state: 'pcCommercialRetentionRate', transform: (v) => Number(v) * 100 },

      { prop: 'group_first_year_commissions', state: 'groupFirstYearCommissions' },
      { prop: 'group_renewal_commissions', state: 'groupRenewalCommissions' },
      { prop: 'group_override_commissions', state: 'groupOverrideCommissions' },
      { prop: 'group_other_revenues', state: 'groupOtherRevenues' },
      { prop: 'group_number_of_cases', state: 'groupNumberOfCases' },
      { prop: 'group_average_case_size', state: 'groupAverageCaseSize' },
      { prop: 'group_case_retention_rate', state: 'groupCaseRetentionRate', transform: (v) => Number(v) * 100 },
    ];
    
    const newFormData = { ...this.state.formData };
    let hasChanges = false;
    
    // Check each financial field for changes
    financialFields.forEach(field => {
      // Special handling for avg_client_age
      if (field.prop === 'avg_client_age') {
        const currentValue = user[field.prop];
        const prevValue = prevProps.user?.[field.prop];
        
        // Only update if new value is not null/undefined and different from previous
        if (currentValue !== null && currentValue !== undefined && currentValue !== prevValue) {
          newFormData[field.state] = currentValue;
          hasChanges = true;
        }
      } else if (user[field.prop] !== undefined && 
          user[field.prop] !== null && 
          user[field.prop] !== prevProps.user?.[field.prop]) {
        newFormData[field.state] = field.transform ? field.transform(user[field.prop]) : user[field.prop];
        hasChanges = true;
      }
    });
    
    if (hasChanges) {
      this.setState({ formData: newFormData });
    }
  }

  componentDidMount() {
    // Add debug logging for features
    const { user } = this.props;
    const companyFeatures = 
      Object.values(window?.__WIZARD_DATA__?.companySettings?.features || []) || 
      user?.company?.features || 
      [];
    
    // Initialize form data with user data if available
    if (user) {
      const formUpdates = {};
      let hasUpdates = false;
      
      // Map of user properties to form field names
      const fieldMapping = {
        'number_of_clients': 'clientCount',
        'avg_client_age': 'averageClientAge',
        'assets_under_management': 'aum',
        'fee_based_revenue': 'feeRevenue',
        'trailer_revenue': 'trailerRevenue',
        'direct_revenue': 'directRevenue',
        'transactional_revenue': 'commissionRevenue',
        'recurring_revenue': 'recurringRevenue',
        'percent_non_recurring_revenue': 'nonRecurringRevenue',
        'premium': 'premium',
        'years_practicing': 'yearsPracticing',
        'annual_revenue': 'annualRevenue',

        // Multi-line fields (no historical)
        'li_first_year_commissions': 'liFirstYearCommissions',
        'li_renewal_commissions': 'liRenewalCommissions',
        'li_override_commissions': 'liOverrideCommissions',
        'li_other_revenues': 'liOtherRevenues',
        'li_policy_count': 'liPolicyCount',
        'pc_personal_lines_premium': 'pcPersonalLinesPremium',
        'pc_commercial_lines_premium': 'pcCommercialLinesPremium',
        'group_first_year_commissions': 'groupFirstYearCommissions',
        'group_renewal_commissions': 'groupRenewalCommissions',
        'group_override_commissions': 'groupOverrideCommissions',
        'group_other_revenues': 'groupOtherRevenues',
        'group_number_of_cases': 'groupNumberOfCases',
        'group_average_case_size': 'groupAverageCaseSize',
      };
      
      // Check each user property and update form data if it exists
      Object.entries(fieldMapping).forEach(([userProp, formField]) => {
        // Special handling for avg_client_age - only update if not null or undefined
        if (userProp === 'avg_client_age') {
          if (user[userProp] !== null && user[userProp] !== undefined) {
            formUpdates[formField] = user[userProp];
            hasUpdates = true;
          }
        } else if (user[userProp] !== undefined && user[userProp] !== null) {
          formUpdates[formField] = user[userProp];
          hasUpdates = true;
        }
      });

      // Percent fields: stored in DB as decimals; UI expects percent
      const percentFieldMap = {
        pc_personal_lines_commission_rate: 'pcPersonalLinesCommissionRate',
        pc_personal_retention_rate: 'pcPersonalRetentionRate',
        pc_commercial_lines_commission_rate: 'pcCommercialLinesCommissionRate',
        pc_commercial_retention_rate: 'pcCommercialRetentionRate',
        group_case_retention_rate: 'groupCaseRetentionRate',
      };
      Object.entries(percentFieldMap).forEach(([userProp, formField]) => {
        if (user[userProp] !== undefined && user[userProp] !== null) {
          formUpdates[formField] = Number(user[userProp]) * 100;
          hasUpdates = true;
        }
      });
      
      // Update state if we have any changes
      if (hasUpdates) {
        this.setState({
          formData: {
            ...this.state.formData,
            ...formUpdates
          }
        });
      }
    }
  }

  handleChange(e) {
    const { name, value } = e.target;
    this.setState({
      formData: {
        ...this.state.formData,
        [name]: value
      },
      errors: {
        ...this.state.errors,
        [name]: null
      }
    });
  }
  
  handleSliderChange(e) {
    const { value } = e.target;
    this.setState({
      formData: {
        ...this.state.formData,
        averageClientAge: parseInt(value)
      },
      errors: {
        ...this.state.errors,
        averageClientAge: null
      }
    });
  }
  
  handleCurrencyChange(field, value) {
    const numValue = parseFloat(value.replace(/[^0-9.]/g, '')) || 0;
    
    this.setState({
      formData: {
        ...this.state.formData,
        [field]: numValue
      },
      errors: {
        ...this.state.errors,
        [field]: null
      }
    });
  }

  handleNumberChange(field, value, { integer = false } = {}) {
    const parsed =
      value === '' || value === null || value === undefined
        ? ''
        : (integer ? parseInt(value, 10) : parseFloat(value));

    this.setState({
      formData: {
        ...this.state.formData,
        [field]: Number.isNaN(parsed) ? '' : parsed,
      },
      errors: {
        ...this.state.errors,
        [field]: null,
      },
    });
  }

  validateForm() {
    const { formData } = this.state;
    const newErrors = {};
    const businessType = this.getBusinessType();
    const { showAUM } = this.getCompanySettings();
    const isMultiLine = this.isMultiLineValuationEnabled();
    const enabledLines = this.enabledLines();
    const showAUMEffective = (!isMultiLine && showAUM) || (isMultiLine && this.isLineFeatureEnabled('wealth'));
    
    if (!formData.clientCount) {
      newErrors.clientCount = this.getTranslatedText('error.client.count.required');
    } else if (isNaN(Number(formData.clientCount)) || Number(formData.clientCount) < 0) {
      newErrors.clientCount = this.getTranslatedText('error.client.count.invalid');
    }
    
    // Ensure averageClientAge has a value, using 45 as default if missing
    if (formData.averageClientAge === null || formData.averageClientAge === undefined) {
      // Instead of showing an error, we'll set the default age of 45
      this.setState({
        formData: {
          ...formData,
          averageClientAge: 45 // Default middle age
        }
      });
    }
    
    if (formData.yearsPracticing === '') {
      newErrors.yearsPracticing = this.getTranslatedText('error.years.practicing.required');
    } else if (isNaN(Number(formData.yearsPracticing)) || Number(formData.yearsPracticing) < 0) {
      newErrors.yearsPracticing = this.getTranslatedText('error.years.practicing.invalid');
    } else if (Number(formData.yearsPracticing) > 70) {
      newErrors.yearsPracticing = this.getTranslatedText('error.years.practicing.max');
    }
    
    // Validate each revenue field against the tenant's max_revenue_input (null = no cap)
    const maxRevenue = this.maxRevenueInput();
    const revFields = [
      'feeRevenue', 'trailerRevenue', 'directRevenue', 'commissionRevenue',
      'premium', 'recurringRevenue', 'nonRecurringRevenue', 'annualRevenue',
    ];
    
    revFields.forEach(field => {
      if (formData[field] && (isNaN(Number(formData[field])) || Number(formData[field]) < 0)) {
        newErrors[field] = this.getTranslatedText('error.amount.invalid');
      } else if (exceedsRevenueCap(formData[field], maxRevenue)) {
        newErrors[field] = revenueCapMessage(maxRevenue, this.getTranslatedText('error.amount.max'));
      }
    });
    
    // Validate AUM separately without max limit if the feature is enabled
    if (showAUMEffective && formData.aum && (isNaN(Number(formData.aum)) || Number(formData.aum) < 0)) {
      newErrors.aum = this.getTranslatedText('error.amount.invalid');
    }

    if (isMultiLine) {
      const hasLine = (line) => Array.isArray(enabledLines) && enabledLines.includes(line);

      const currencyLikeFieldsByLine = {
        wealth: ['feeRevenue', 'trailerRevenue', 'directRevenue', 'commissionRevenue'],
        life: ['liFirstYearCommissions', 'liRenewalCommissions', 'liOverrideCommissions', 'liOtherRevenues'],
        pc: ['pcPersonalLinesPremium', 'pcCommercialLinesPremium'],
        group: ['groupFirstYearCommissions', 'groupRenewalCommissions', 'groupOverrideCommissions', 'groupOtherRevenues', 'groupAverageCaseSize'],
      };

      const countFieldsByLine = {
        life: [{ field: 'liPolicyCount', max: 1000000 }],
        group: [{ field: 'groupNumberOfCases', max: 1000000 }],
      };

      const percentFieldsByLine = {
        pc: ['pcPersonalLinesCommissionRate', 'pcPersonalRetentionRate', 'pcCommercialLinesCommissionRate', 'pcCommercialRetentionRate'],
        group: ['groupCaseRetentionRate'],
      };

      const validateCurrency = (field) => {
        const v = Number(formData[field]);
        if (formData[field] === '' || formData[field] === null || formData[field] === undefined) return;
        if (isNaN(v) || v < 0) newErrors[field] = this.getTranslatedText('error.amount.invalid');
        else if (exceedsRevenueCap(v, maxRevenue)) newErrors[field] = revenueCapMessage(maxRevenue, this.getTranslatedText('error.amount.max'));
      };

      const validatePercent = (field) => {
        const v = Number(formData[field]);
        if (formData[field] === '' || formData[field] === null || formData[field] === undefined) return;
        if (isNaN(v) || v < 0) newErrors[field] = this.getTranslatedText('error.amount.invalid');
        else if (v > 100) newErrors[field] = this.getTranslatedText('error.commission.percentage.max');
      };

      const validateCount = (field, max) => {
        const v = Number(formData[field]);
        if (formData[field] === '' || formData[field] === null || formData[field] === undefined) return;
        if (!Number.isFinite(v) || v < 0 || !Number.isInteger(v)) newErrors[field] = this.getTranslatedText('error.client.count.invalid');
        else if (max && v > max) newErrors[field] = this.getTranslatedText('error.amount.max').replace('%{max}', Number(max).toLocaleString());
      };

      Object.entries(currencyLikeFieldsByLine).forEach(([line, fields]) => {
        if (!hasLine(line)) return;
        fields.forEach(validateCurrency);
      });

      Object.entries(percentFieldsByLine).forEach(([line, fields]) => {
        if (!hasLine(line)) return;
        fields.forEach(validatePercent);
      });

      Object.entries(countFieldsByLine).forEach(([line, items]) => {
        if (!hasLine(line)) return;
        items.forEach(({ field, max }) => validateCount(field, max));
      });
    }
    
    this.setState({ errors: newErrors });
    return Object.keys(newErrors).length === 0;
  }

  handleSubmit(e) {
    e.preventDefault();
    
    if (!this.validateForm()) return;
    
    this.setState({ isSubmitting: true });
    
    const { formData } = this.state;
    const { showWealthLifeFields, showPCFields, showAUM } = this.getCompanySettings();
    const businessType = this.getBusinessType();
    const isMultiLine = this.isMultiLineValuationEnabled();
    const enabledLines = this.enabledLines();
    const showAUMEffective = (!isMultiLine && showAUM) || (isMultiLine && this.isLineFeatureEnabled('wealth'));
    
    // Prepare variables for GraphQL mutation based on visible fields
    const variables = {
      number_of_clients: Number(formData.clientCount),
      avg_client_age: Number(formData.averageClientAge),
      years_practicing: Number(formData.yearsPracticing),
      locale: this.props.currentLanguage
    };
    
    // Only include AUM if feature is enabled
    if (showAUMEffective) {
      variables.assets_under_management = Number(formData.aum) || 0;
    }
    
    // Multi-line onboarding mode: include fields for each enabled line (no historical)
    if (isMultiLine) {
      if (Array.isArray(enabledLines) && enabledLines.includes('wealth')) {
        Object.assign(variables, {
          fee_based_revenue: Number(formData.feeRevenue) || 0,
          trailer_revenue: Number(formData.trailerRevenue) || 0,
          direct_revenue: Number(formData.directRevenue) || 0,
          transactional_revenue: Number(formData.commissionRevenue) || 0,
          // Keep legacy top-level recurring_revenue in sync for downstream UI
          recurring_revenue: (Number(formData.feeRevenue) || 0) + (Number(formData.trailerRevenue) || 0) + (Number(formData.directRevenue) || 0),
        });
      }

      if (Array.isArray(enabledLines) && enabledLines.includes('life')) {
        Object.assign(variables, {
          li_first_year_commissions: Number(formData.liFirstYearCommissions) || 0,
          li_renewal_commissions: Number(formData.liRenewalCommissions) || 0,
          li_override_commissions: Number(formData.liOverrideCommissions) || 0,
          li_other_revenues: Number(formData.liOtherRevenues) || 0,
          li_policy_count: Number(formData.liPolicyCount) || 0,
        });
      }

      if (Array.isArray(enabledLines) && enabledLines.includes('pc')) {
        // Rates/retention are entered as percent in onboarding (aligned with RevenueInformationEditModal);
        // normalization (percent -> decimal) is handled server-side.
        Object.assign(variables, {
          pc_personal_lines_premium: Number(formData.pcPersonalLinesPremium) || 0,
          pc_personal_lines_commission_rate: Number(formData.pcPersonalLinesCommissionRate) || 0,
          pc_personal_retention_rate: Number(formData.pcPersonalRetentionRate) || 0,
          pc_commercial_lines_premium: Number(formData.pcCommercialLinesPremium) || 0,
          pc_commercial_lines_commission_rate: Number(formData.pcCommercialLinesCommissionRate) || 0,
          pc_commercial_retention_rate: Number(formData.pcCommercialRetentionRate) || 0,
        });
      }

      if (Array.isArray(enabledLines) && enabledLines.includes('group')) {
        Object.assign(variables, {
          group_first_year_commissions: Number(formData.groupFirstYearCommissions) || 0,
          group_renewal_commissions: Number(formData.groupRenewalCommissions) || 0,
          group_override_commissions: Number(formData.groupOverrideCommissions) || 0,
          group_other_revenues: Number(formData.groupOtherRevenues) || 0,
          group_number_of_cases: Number(formData.groupNumberOfCases) || 0,
          group_average_case_size: Number(formData.groupAverageCaseSize) || 0,
          group_case_retention_rate: Number(formData.groupCaseRetentionRate) || 0,
        });
      }
    } else if (showWealthLifeFields) {
      Object.assign(variables, {
        fee_based_revenue: Number(formData.feeRevenue) || 0,
        trailer_revenue: Number(formData.trailerRevenue) || 0,
        direct_revenue: Number(formData.directRevenue) || 0,
        transactional_revenue: Number(formData.commissionRevenue) || 0,
        recurring_revenue: Number(formData.feeRevenue) + Number(formData.trailerRevenue) || 0,
      });
      
      // Only add AUM if the feature is enabled (it's already added above, so we don't need to add it again)
    } else if (showPCFields) {
      if (businessType === 'p_and_c') {
        // For p_and_c business type, only include annual_revenue and recurring revenue
        Object.assign(variables, {
          annual_revenue: Number(formData.annualRevenue) || 0,
          recurring_revenue: Number(formData.recurringRevenue) || 0,
        });
      } else {
        // For other PC business types, include all P&C fields
        Object.assign(variables, {
          recurring_revenue: Number(formData.recurringRevenue) || 0,
          premium: Number(formData.premium) || 0,
          percent_non_recurring_revenue: Number(formData.nonRecurringRevenue) || 0
        });
      }
    } else {
      // Default fields when no specific type is enabled
      Object.assign(variables, {
        fee_based_revenue: Number(formData.feeRevenue) || 0,
        transactional_revenue: Number(formData.commissionRevenue) || 0,
      });
      
      // AUM is already handled above
    }
    
    // Store all production fields in wizard data for other components to use
    const localData = {
      number_of_clients: Number(formData.clientCount),
      average_client_age: formData.averageClientAge,
      years_practicing: Number(formData.yearsPracticing),
      ...variables
    };
    
    // Store data in window.__WIZARD_DATA__ for other components
    if (window.__WIZARD_DATA__) {
      window.__WIZARD_DATA__.resumeData = {
        ...window.__WIZARD_DATA__.resumeData,
        ...localData
      };
    }
    
    // Execute the GraphQL mutation
    this.props.mutate({ variables })
      .then(response => {
        this.setState({ isSubmitting: false });
        
        // Update user data in parent component
        const userData = {
          ...localData,
          id: this.props.user?.id
        };
        
        if (this.props.onUserUpdate) {
          this.props.onUserUpdate(userData);
        }
        
        // Move to next step (PracticeInformationStep) instead of completing onboarding
        if (this.props.onNext) this.props.onNext();
      })
      .catch(error => {
        this.setState({ 
          isSubmitting: false,
          errors: { 
            general: error.message ? error.message.replace('GraphQL error:', '') : 'An error occurred'
          }
        });
      });
  }

  handleBack() {
    // Prefer props navigation (from OnboardingWizard) over context navigation
    // This ensures proper synchronization when used inside OnboardingWizard
    if (typeof this.props.onBack === 'function') {
      this.props.onBack();
      return;
    }
    
    // Try using context navigation as fallback
    const { onboardingContext } = this.props;
    if (onboardingContext && typeof onboardingContext.goToPreviousStep === 'function') {
      onboardingContext.goToPreviousStep();
      return;
    }
    
    // Try using prevStep from context as alternative fallback
    if (onboardingContext && typeof onboardingContext.prevStep === 'function') {
      onboardingContext.prevStep();
      return;
    }
    
    // Final fallback to direct URL navigation
    window.location.href = '/onboarding/motivation';
  }

  // Format currency inputs
  formatCurrency(value) {
    if (value === null || value === undefined || value === '') return '';
    return Number(value).toLocaleString('en-US', { 
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  }
  
  // Calculate total revenue for wealth/life fields
  calculateWealthTotal() {
    const { formData } = this.state;
    return (
      Number(formData.feeRevenue || 0) + 
      Number(formData.trailerRevenue || 0) + 
      Number(formData.directRevenue || 0) + 
      Number(formData.commissionRevenue || 0)
    );
  }
  
  // Calculate total revenue for P&C fields
  calculatePCTotal() {
    const { formData } = this.state;
    const businessType = this.getBusinessType();
    
    // For p_and_c business type, we only show Annual Revenue and Recurring Revenue
    if (businessType === 'p_and_c') {
      return Number(formData.annualRevenue || 0) + Number(formData.recurringRevenue || 0);
    }
    
    // For other business types, calculate using recurring and non-recurring revenue
    return (
      Number(formData.recurringRevenue || 0) + 
      Number(formData.nonRecurringRevenue || 0)
    );
  }
  
  // Calculate total for default case
  calculateDefaultTotal() {
    const { formData } = this.state;
    return (
      Number(formData.feeRevenue || 0) + 
      Number(formData.commissionRevenue || 0)
    );
  }

  // Multi-line totals (no historical)
  calculateLifeTotal() {
    const { formData } = this.state;
    return (
      Number(formData.liFirstYearCommissions || 0) +
      Number(formData.liRenewalCommissions || 0) +
      Number(formData.liOverrideCommissions || 0) +
      Number(formData.liOtherRevenues || 0)
    );
  }

  calculateGroupTotal() {
    const { formData } = this.state;
    return (
      Number(formData.groupFirstYearCommissions || 0) +
      Number(formData.groupRenewalCommissions || 0) +
      Number(formData.groupOverrideCommissions || 0) +
      Number(formData.groupOtherRevenues || 0)
    );
  }

  calculatePCTotalFromPremiums() {
    const { formData } = this.state;
    const personal =
      Number(formData.pcPersonalLinesPremium || 0) *
      (Number(formData.pcPersonalLinesCommissionRate || 0) / 100);
    const commercial =
      Number(formData.pcCommercialLinesPremium || 0) *
      (Number(formData.pcCommercialLinesCommissionRate || 0) / 100);
    return personal + commercial;
  }

  calculateMultiLineTotal() {
    const lines = this.enabledLines();
    const totals = {
      wealth: this.calculateWealthTotal(),
      life: this.calculateLifeTotal(),
      pc: this.calculatePCTotalFromPremiums(),
      group: this.calculateGroupTotal(),
    };
    return (lines || []).reduce((sum, line) => sum + Number(totals[line] || 0), 0);
  }

  // Get translated text based on current language
  getTranslatedText(key) {
    const { currentLanguage } = this.props;
    
    // Define translations
    const translations = {
      en: {
        'title': 'Production Information',
        'subtitle': 'This information helps us understand your practice and provide the most relevant opportunities.',
        'practice.information': 'Practice Information',
        'years.in.business': 'Years in Business',
        'number.of.clients': 'Number of Clients',
        'Commission Percentage.percentage': 'Commission Percentage',
        'client.demographics': 'Client Demographics',
        'average.client.age': 'Average Client Age:',
        'production.summary': 'Production Summary',
        'production.summary.subtitle': 'The following production summary has been pulled for your business. If this needs to be adjusted, please use the input boxes below.',
        'aum': 'Assets Under Management (AUM)',
        'fee.based.revenue': 'Fee Based Revenue',
        'trails.revenue': 'Trails Revenue',
        'direct.revenue': 'Direct Revenue',
        'commission.revenue': 'Commission Revenue',
        'fee.revenue': 'Fee Revenue',
        'annual.revenue': 'Annual Revenue',
        'recurring.revenue': 'Recurring Revenue',
        'non.recurring.revenue': 'Non-Recurring Revenue',
        'premium': 'Premium',
        'total.production': 'Total Production',
        'back': 'Back',
        'continue': 'Continue',
        'saving': 'Saving...',
        'required': 'required',
        'error.client.count.required': 'Please enter your client count',
        'error.client.count.invalid': 'Please enter a valid number',
        'error.average.client.age': 'Please select the average age of your clients',
        'error.years.practicing.required': 'Please enter years practicing',
        'error.years.practicing.invalid': 'Please enter a valid number of years',
        'error.years.practicing.max': 'Value should not exceed 70 years',
        'error.commission.percentage.required': 'Please enter your commission percentage',
        'error.commission.percentage.invalid': 'Please enter a valid percentage',
        'error.commission.percentage.max': 'Percentage cannot be greater than 100%',
        'error.amount.invalid': 'Please enter a valid amount',
        'error.amount.max': 'Value should not exceed %{max}'
      },
      fr: {
        'title': 'Informations sur la Production',
        'subtitle': 'Ces informations nous aident à comprendre votre pratique et à fournir les opportunités les plus pertinentes.',
        'practice.information': 'Informations sur la Pratique',
        'years.in.business': 'Années en Activité',
        'number.of.clients': 'Nombre de Clients',
        'commission.percentage': 'Pourcentage de Commission',
        'client.demographics': 'Démographie des Clients',
        'average.client.age': 'Âge Moyen des Clients:',
        'production.summary': 'Résumé de la Production',
        'production.summary.subtitle': 'Le résumé de production suivant a été extrait pour votre entreprise. Si cela doit être ajusté, veuillez utiliser les boîtes de saisie ci-dessous.',
        'aum': 'Actifs Sous Gestion (ASG)',
        'fee.based.revenue': 'Revenus Basés sur les Frais',
        'trails.revenue': 'Revenus de Traînage',
        'direct.revenue': 'Revenu Direct',
        'commission.revenue': 'Revenu de Commission',
        'fee.revenue': 'Revenu de Frais',
        'annual.revenue': 'Revenu Annuel',
        'recurring.revenue': 'Revenu Récurrent',
        'non.recurring.revenue': 'Revenu Non Récurrent',
        'premium': 'Prime',
        'total.production': 'Production Totale',
        'back': 'Retour',
        'continue': 'Continuer',
        'saving': 'Enregistrement...',
        'required': 'requis',
        'error.client.count.required': 'Veuillez entrer votre nombre de clients',
        'error.client.count.invalid': 'Veuillez entrer un nombre valide',
        'error.average.client.age': 'Veuillez sélectionner l\'âge moyen de vos clients',
        'error.years.practicing.required': 'Veuillez entrer les années de pratique',
        'error.years.practicing.invalid': 'Veuillez entrer un nombre valide d\'années',
        'error.years.practicing.max': 'La valeur ne doit pas dépasser 70 ans',
        'error.commission.percentage.required': 'Veuillez entrer votre pourcentage de commission',
        'error.commission.percentage.invalid': 'Veuillez entrer un pourcentage valide',
        'error.commission.percentage.max': 'Le pourcentage ne peut pas être supérieur à 100%',
        'error.amount.invalid': 'Veuillez entrer un montant valide',
        'error.amount.max': 'La valeur ne doit pas dépasser 12 000 000 $'
      },
      'en-GB': {
        'title': 'Production Information',
        'subtitle': 'This information helps us understand your practice and provide the most relevant opportunities.',
        'practice.information': 'Practice Information',
        'years.in.business': 'Years in Business',
        'number.of.clients': 'Number of Clients',
        'Commission Percentage.percentage': 'Commission Percentage',
        'client.demographics': 'Client Demographics',
        'average.client.age': 'Average Client Age:',
        'production.summary': 'Production Summary',
        'production.summary.subtitle': 'The following production summary has been pulled for your business. If this needs to be adjusted, please use the input boxes below.',
        'aum': 'Funds Under Management (FUM)',
        'fee.based.revenue': 'Fee Based Revenue',
        'trails.revenue': 'Trails Revenue',
        'direct.revenue': 'Direct Revenue',
        'commission.revenue': 'New Business Income',
        'fee.revenue': 'Recurring Income',
        'annual.revenue': 'Annual Revenue',
        'recurring.revenue': 'Recurring Revenue',
        'non.recurring.revenue': 'Non-Recurring Revenue',
        'premium': 'Premium',
        'total.production': 'Total Production',
        'back': 'Back',
        'continue': 'Continue',
        'saving': 'Saving...',
        'required': 'required',
        'error.client.count.required': 'Please enter your client count',
        'error.client.count.invalid': 'Please enter a valid number',
        'error.average.client.age': 'Please select the average age of your clients',
        'error.years.practicing.required': 'Please enter years practicing',
        'error.years.practicing.invalid': 'Please enter a valid number of years',
        'error.years.practicing.max': 'Value should not exceed 70 years',
        'error.commission.percentage.required': 'Please enter your commission percentage',
        'error.commission.percentage.invalid': 'Please enter a valid percentage',
        'error.commission.percentage.max': 'Percentage cannot be greater than 100%',
        'error.amount.invalid': 'Please enter a valid amount',
        'error.amount.max': 'Value should not exceed %{max}'
      }
    };
    
    // Return translated text or default to English
    return translations[currentLanguage]?.[key] || translations['en'][key] || key;
  }

  render() {
    const { formData, errors, isSubmitting } = this.state;
    const loading = this.props.loading || this.companyRevenueSettingsLoading();
    const { showWealthLifeFields, showPCFields, showAUM } = this.getCompanySettings();
    const businessType = this.getBusinessType();
    const isMultiLine = this.isMultiLineValuationEnabled();
    const enabledLines = this.enabledLines();
    const lineTabs = (enabledLines || []).map((line) => ({
      id: line,
      label: LINE_LABELS[line] || line,
    }));
    const showWealthLifeFieldsEffective = !isMultiLine && showWealthLifeFields;
    const showPCFieldsEffective = !isMultiLine && showPCFields;
    const showAUMEffective = (!isMultiLine && showAUM) || (isMultiLine && this.isLineFeatureEnabled('wealth'));
    const wealthTotal = this.calculateWealthTotal();
    const pcTotal = this.calculatePCTotal();
    const defaultTotal = this.calculateDefaultTotal();
    
    return (
      <div className="bg-succession-bg-primary border border-succession-border shadow-succession-lg rounded-xl p-8" style={{ backgroundColor: 'var(--succession-bg-primary, #ffffff)' }}>
        <h2 className="text-2xl font-medium text-succession-text-primary mb-3 text-center">{this.getTranslatedText('title')}</h2>
        <p className="text-md text-succession-text-secondary mb-8 text-center max-w-2xl mx-auto">
          {this.getTranslatedText('subtitle')}
        </p>
        
        {errors.general && (
          <div className="mb-6 p-4 bg-succession-error-bg border border-red-200 rounded-lg shadow-sm">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-succession-error mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 9a1 1 0 01-1-1v-4a1 1 0 112 0v4a1 1 0 01-1 1z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-succession-error">{errors.general}</p>
            </div>
          </div>
        )}
        
        <form onSubmit={this.handleSubmit} className="space-y-8">
          <div className="space-y-4 border border-succession-border rounded-lg p-6 shadow-succession-sm bg-succession-bg-primary" style={{ backgroundColor: 'var(--succession-bg-primary, #ffffff)' }}>
            <h3 className="text-lg font-medium text-succession-text-primary border-b border-succession-border pb-3">{this.getTranslatedText('practice.information')}</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="yearsPracticing" className="block text-sm font-medium text-succession-text-primary mb-1">
                  {this.getTranslatedText('years.in.business')} <span className="text-red-500">*</span>
                </label>
                <select
                  id="yearsPracticing"
                  name="yearsPracticing"
                  value={formData.yearsPracticing}
                  onChange={this.handleChange}
                  className={`w-full px-4 py-2.5 border rounded-lg shadow-sm focus:ring-succession-primary focus:border-succession-primary transition-colors bg-succession-bg-primary text-succession-text-primary ${errors.yearsPracticing ? 'border-succession-error bg-succession-error-bg' : 'border-succession-border'}`}
                  style={{ backgroundColor: 'var(--succession-bg-primary, #ffffff)' }}
                >
                  <option value="">Select years in business</option>
                  {Array.from({ length: 70 }, (_, i) => i + 1).map(year => (
                    <option key={year} value={year}>
                      {year} year{year !== 1 ? 's' : ''}
                    </option>
                  ))}
                </select>
                {errors.yearsPracticing && <div className="mt-1.5 text-sm text-red-600 flex items-center"><svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 9a1 1 0 01-1-1v-4a1 1 0 112 0v4a1 1 0 01-1 1z" clipRule="evenodd" /></svg>{errors.yearsPracticing}</div>}
              </div>
              
              <div>
                <label htmlFor="clientCount" className="block text-sm font-medium text-succession-text-primary mb-1">
                  {this.getTranslatedText('number.of.clients')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  id="clientCount"
                  name="clientCount"
                  value={formData.clientCount}
                  onChange={this.handleChange}
                  className={`w-full px-4 py-2.5 border rounded-lg shadow-sm focus:ring-succession-primary focus:border-succession-primary transition-colors bg-succession-bg-primary text-succession-text-primary ${errors.clientCount ? 'border-succession-error bg-succession-error-bg' : 'border-succession-border'}`}
                  style={{ backgroundColor: 'var(--succession-bg-primary, #ffffff)' }}
                  placeholder="e.g. 100"
                />
                {errors.clientCount && <div className="mt-1.5 text-sm text-red-600 flex items-center"><svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 9a1 1 0 01-1-1v-4a1 1 0 112 0v4a1 1 0 ai-1 1z" clipRule="evenodd" /></svg>{errors.clientCount}</div>}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-succession-text-primary mb-2">
                {this.getTranslatedText('average.client.age')} <span className="text-succession-primary font-medium">{formData.averageClientAge}</span>
              </label>
              {errors.averageClientAge && <div className="mb-2 text-sm text-succession-error flex items-center"><svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 9a1 1 0 ai-1-1v-4a1 1 0 112 0v4a1 1 0 ai-1 1z" clipRule="evenodd" /></svg>{errors.averageClientAge}</div>}
              
              <div className="px-2 mt-4">
                <input
                  type="range"
                  min="25"
                  max="85"
                  step="1"
                  value={formData.averageClientAge}
                  onChange={this.handleSliderChange}
                  className="w-full h-2 bg-gray-200 border border-succession-border rounded-lg appearance-none cursor-pointer accent-succession-primary"
                  style={{ accentColor: 'var(--succession-primary, #5489B6)' }}
                />
                
                <div className="flex justify-between mt-2 text-xs text-succession-text-muted">
                  {this.ageMarkers.map(marker => (
                    <div key={marker.value} className="text-center">
                      <div className={`${formData.averageClientAge === marker.value ? 'text-succession-primary font-medium' : ''}`}>
                        {marker.label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* AUM Field - show if enabled (multi-line: show when wealth line is enabled) */}
            {showAUMEffective && (
              <div>
                <label className="block text-sm font-medium text-succession-text-primary mb-1">
                  {this.getTranslatedText('aum')}
                </label>
                <div className="flex items-center">
                  <div className="px-4 py-2.5 bg-succession-bg-secondary text-succession-text-muted font-medium border border-r-0 border-succession-border rounded-l-lg">{currencySymbol()}</div>
                  <input
                    type="text"
                    className="w-full px-4 py-2.5 border border-succession-border rounded-r-lg shadow-sm focus:ring-succession-primary focus:border-succession-primary transition-colors bg-succession-bg-primary text-succession-text-primary"
                    value={this.formatCurrency(formData.aum)}
                    onChange={(e) => this.handleCurrencyChange('aum', e.target.value)}
                  />
                </div>
                {errors.aum && <div className="mt-1.5 text-sm text-red-600 flex items-center"><svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 9a1 1 0 ai-1-1v-4a1 1 0 112 0v4a1 1 0 ai-1 1z" clipRule="evenodd" /></svg>{errors.aum}</div>}
              </div>
            )}
          </div>
          
          {/* Production Summary Section */}
          <div className="space-y-4 border border-succession-border rounded-lg p-6 shadow-succession-sm bg-succession-bg-primary" style={{ backgroundColor: 'var(--succession-bg-primary, #ffffff)' }}>
            <h3 className="text-lg font-medium text-succession-text-primary border-b border-succession-border pb-3">{this.getTranslatedText('production.summary')}</h3>
            <p className="text-sm text-succession-text-secondary mb-4">
              {this.getTranslatedText('production.summary.subtitle')}
            </p>
            
            {/* Multi-line Revenue (no historical) */}
            {isMultiLine ? (
              <div className="space-y-4">
                {Array.isArray(enabledLines) && enabledLines.length > 1 && (
                  <Tabs
                    tabs={lineTabs}
                    value={this.state.activeLine}
                    onChange={(next) => this.setState({ activeLine: next })}
                    showDescription={false}
                    variant="outline"
                    size="sm"
                  />
                )}

                {/* Wealth */}
                {this.state.activeLine === 'wealth' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-succession-text-primary mb-1">
                        {this.wealthRevenueLabel('fee.based.revenue', 'fee_based_revenue')}
                      </label>
                      <div className="flex items-center">
                        <div className="px-4 py-2.5 bg-succession-bg-secondary text-succession-text-muted font-medium border border-r-0 border-succession-border rounded-l-lg">{currencySymbol()}</div>
                        <input
                          type="text"
                          className="w-full px-4 py-2.5 border border-succession-border rounded-r-lg shadow-sm focus:ring-succession-primary focus:border-succession-primary transition-colors bg-succession-bg-primary text-succession-text-primary"
                          value={this.formatCurrency(formData.feeRevenue)}
                          onChange={(e) => this.handleCurrencyChange('feeRevenue', e.target.value)}
                        />
                      </div>
                      {errors.feeRevenue && <div className="mt-1.5 text-sm text-red-600">{errors.feeRevenue}</div>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-succession-text-primary mb-1">
                        {this.wealthRevenueLabel('trails.revenue', 'trailer_revenue')}
                      </label>
                      <div className="flex items-center">
                        <div className="px-4 py-2.5 bg-succession-bg-secondary text-succession-text-muted font-medium border border-r-0 border-succession-border rounded-l-lg">{currencySymbol()}</div>
                        <input
                          type="text"
                          className="w-full px-4 py-2.5 border border-succession-border rounded-r-lg shadow-sm focus:ring-succession-primary focus:border-succession-primary transition-colors bg-succession-bg-primary text-succession-text-primary"
                          value={this.formatCurrency(formData.trailerRevenue)}
                          onChange={(e) => this.handleCurrencyChange('trailerRevenue', e.target.value)}
                        />
                      </div>
                      {errors.trailerRevenue && <div className="mt-1.5 text-sm text-red-600">{errors.trailerRevenue}</div>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-succession-text-primary mb-1">
                        {this.wealthRevenueLabel('direct.revenue', 'direct_revenue')}
                      </label>
                      <div className="flex items-center">
                        <div className="px-4 py-2.5 bg-succession-bg-secondary text-succession-text-muted font-medium border border-r-0 border-succession-border rounded-l-lg">{currencySymbol()}</div>
                        <input
                          type="text"
                          className="w-full px-4 py-2.5 border border-succession-border rounded-r-lg shadow-sm focus:ring-succession-primary focus:border-succession-primary transition-colors bg-succession-bg-primary text-succession-text-primary"
                          value={this.formatCurrency(formData.directRevenue)}
                          onChange={(e) => this.handleCurrencyChange('directRevenue', e.target.value)}
                        />
                      </div>
                      {errors.directRevenue && <div className="mt-1.5 text-sm text-red-600">{errors.directRevenue}</div>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-succession-text-primary mb-1">
                        {this.wealthRevenueLabel('commission.revenue', 'transactional_revenue')}
                      </label>
                      <div className="flex items-center">
                        <div className="px-4 py-2.5 bg-succession-bg-secondary text-succession-text-muted font-medium border border-r-0 border-succession-border rounded-l-lg">{currencySymbol()}</div>
                        <input
                          type="text"
                          className="w-full px-4 py-2.5 border border-succession-border rounded-r-lg shadow-sm focus:ring-succession-primary focus:border-succession-primary transition-colors bg-succession-bg-primary text-succession-text-primary"
                          value={this.formatCurrency(formData.commissionRevenue)}
                          onChange={(e) => this.handleCurrencyChange('commissionRevenue', e.target.value)}
                        />
                      </div>
                      {errors.commissionRevenue && <div className="mt-1.5 text-sm text-red-600">{errors.commissionRevenue}</div>}
                    </div>
                  </div>
                )}

                {/* Life */}
                {this.state.activeLine === 'life' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-succession-text-primary mb-1">First Year Commissions</label>
                      <div className="flex items-center">
                        <div className="px-4 py-2.5 bg-succession-bg-secondary text-succession-text-muted font-medium border border-r-0 border-succession-border rounded-l-lg">{currencySymbol()}</div>
                        <input
                          type="text"
                          className="w-full px-4 py-2.5 border border-succession-border rounded-r-lg shadow-sm focus:ring-succession-primary focus:border-succession-primary transition-colors bg-succession-bg-primary text-succession-text-primary"
                          value={this.formatCurrency(formData.liFirstYearCommissions)}
                          onChange={(e) => this.handleCurrencyChange('liFirstYearCommissions', e.target.value)}
                        />
                      </div>
                      {errors.liFirstYearCommissions && <div className="mt-1.5 text-sm text-red-600">{errors.liFirstYearCommissions}</div>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-succession-text-primary mb-1">Renewal Commissions</label>
                      <div className="flex items-center">
                        <div className="px-4 py-2.5 bg-succession-bg-secondary text-succession-text-muted font-medium border border-r-0 border-succession-border rounded-l-lg">{currencySymbol()}</div>
                        <input
                          type="text"
                          className="w-full px-4 py-2.5 border border-succession-border rounded-r-lg shadow-sm focus:ring-succession-primary focus:border-succession-primary transition-colors bg-succession-bg-primary text-succession-text-primary"
                          value={this.formatCurrency(formData.liRenewalCommissions)}
                          onChange={(e) => this.handleCurrencyChange('liRenewalCommissions', e.target.value)}
                        />
                      </div>
                      {errors.liRenewalCommissions && <div className="mt-1.5 text-sm text-red-600">{errors.liRenewalCommissions}</div>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-succession-text-primary mb-1">Override Commissions</label>
                      <div className="flex items-center">
                        <div className="px-4 py-2.5 bg-succession-bg-secondary text-succession-text-muted font-medium border border-r-0 border-succession-border rounded-l-lg">{currencySymbol()}</div>
                        <input
                          type="text"
                          className="w-full px-4 py-2.5 border border-succession-border rounded-r-lg shadow-sm focus:ring-succession-primary focus:border-succession-primary transition-colors bg-succession-bg-primary text-succession-text-primary"
                          value={this.formatCurrency(formData.liOverrideCommissions)}
                          onChange={(e) => this.handleCurrencyChange('liOverrideCommissions', e.target.value)}
                        />
                      </div>
                      {errors.liOverrideCommissions && <div className="mt-1.5 text-sm text-red-600">{errors.liOverrideCommissions}</div>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-succession-text-primary mb-1">Other Revenues</label>
                      <div className="flex items-center">
                        <div className="px-4 py-2.5 bg-succession-bg-secondary text-succession-text-muted font-medium border border-r-0 border-succession-border rounded-l-lg">{currencySymbol()}</div>
                        <input
                          type="text"
                          className="w-full px-4 py-2.5 border border-succession-border rounded-r-lg shadow-sm focus:ring-succession-primary focus:border-succession-primary transition-colors bg-succession-bg-primary text-succession-text-primary"
                          value={this.formatCurrency(formData.liOtherRevenues)}
                          onChange={(e) => this.handleCurrencyChange('liOtherRevenues', e.target.value)}
                        />
                      </div>
                      {errors.liOtherRevenues && <div className="mt-1.5 text-sm text-red-600">{errors.liOtherRevenues}</div>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-succession-text-primary mb-1">Policy Count</label>
                      <input
                        type="number"
                        className="w-full px-4 py-2.5 border border-succession-border rounded-lg shadow-sm focus:ring-succession-primary focus:border-succession-primary transition-colors bg-succession-bg-primary text-succession-text-primary"
                        value={formData.liPolicyCount}
                        onChange={(e) => this.handleNumberChange('liPolicyCount', e.target.value, { integer: true })}
                      />
                      {errors.liPolicyCount && <div className="mt-1.5 text-sm text-red-600">{errors.liPolicyCount}</div>}
                    </div>
                  </div>
                )}

                {/* P&C */}
                {this.state.activeLine === 'pc' && (
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-sm font-semibold text-succession-text-primary mb-2">Personal Lines</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-succession-text-primary mb-1">Premium</label>
                          <div className="flex items-center">
                            <div className="px-4 py-2.5 bg-succession-bg-secondary text-succession-text-muted font-medium border border-r-0 border-succession-border rounded-l-lg">{currencySymbol()}</div>
                            <input
                              type="text"
                              className="w-full px-4 py-2.5 border border-succession-border rounded-r-lg shadow-sm focus:ring-succession-primary focus:border-succession-primary transition-colors bg-succession-bg-primary text-succession-text-primary"
                              value={this.formatCurrency(formData.pcPersonalLinesPremium)}
                              onChange={(e) => this.handleCurrencyChange('pcPersonalLinesPremium', e.target.value)}
                            />
                          </div>
                          {errors.pcPersonalLinesPremium && <div className="mt-1.5 text-sm text-red-600">{errors.pcPersonalLinesPremium}</div>}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-succession-text-primary mb-1">Commission Rate (%)</label>
                          <input
                            type="number"
                            step="0.1"
                            className="w-full px-4 py-2.5 border border-succession-border rounded-lg shadow-sm focus:ring-succession-primary focus:border-succession-primary transition-colors bg-succession-bg-primary text-succession-text-primary"
                            value={formData.pcPersonalLinesCommissionRate}
                            onChange={(e) => this.handleNumberChange('pcPersonalLinesCommissionRate', e.target.value)}
                          />
                          {errors.pcPersonalLinesCommissionRate && <div className="mt-1.5 text-sm text-red-600">{errors.pcPersonalLinesCommissionRate}</div>}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-succession-text-primary mb-1">Retention Rate (%)</label>
                          <input
                            type="number"
                            step="0.1"
                            className="w-full px-4 py-2.5 border border-succession-border rounded-lg shadow-sm focus:ring-succession-primary focus:border-succession-primary transition-colors bg-succession-bg-primary text-succession-text-primary"
                            value={formData.pcPersonalRetentionRate}
                            onChange={(e) => this.handleNumberChange('pcPersonalRetentionRate', e.target.value)}
                          />
                          {errors.pcPersonalRetentionRate && <div className="mt-1.5 text-sm text-red-600">{errors.pcPersonalRetentionRate}</div>}
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-sm font-semibold text-succession-text-primary mb-2">Commercial Lines</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-succession-text-primary mb-1">Premium</label>
                          <div className="flex items-center">
                            <div className="px-4 py-2.5 bg-succession-bg-secondary text-succession-text-muted font-medium border border-r-0 border-succession-border rounded-l-lg">{currencySymbol()}</div>
                            <input
                              type="text"
                              className="w-full px-4 py-2.5 border border-succession-border rounded-r-lg shadow-sm focus:ring-succession-primary focus:border-succession-primary transition-colors bg-succession-bg-primary text-succession-text-primary"
                              value={this.formatCurrency(formData.pcCommercialLinesPremium)}
                              onChange={(e) => this.handleCurrencyChange('pcCommercialLinesPremium', e.target.value)}
                            />
                          </div>
                          {errors.pcCommercialLinesPremium && <div className="mt-1.5 text-sm text-red-600">{errors.pcCommercialLinesPremium}</div>}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-succession-text-primary mb-1">Commission Rate (%)</label>
                          <input
                            type="number"
                            step="0.1"
                            className="w-full px-4 py-2.5 border border-succession-border rounded-lg shadow-sm focus:ring-succession-primary focus:border-succession-primary transition-colors bg-succession-bg-primary text-succession-text-primary"
                            value={formData.pcCommercialLinesCommissionRate}
                            onChange={(e) => this.handleNumberChange('pcCommercialLinesCommissionRate', e.target.value)}
                          />
                          {errors.pcCommercialLinesCommissionRate && <div className="mt-1.5 text-sm text-red-600">{errors.pcCommercialLinesCommissionRate}</div>}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-succession-text-primary mb-1">Retention Rate (%)</label>
                          <input
                            type="number"
                            step="0.1"
                            className="w-full px-4 py-2.5 border border-succession-border rounded-lg shadow-sm focus:ring-succession-primary focus:border-succession-primary transition-colors bg-succession-bg-primary text-succession-text-primary"
                            value={formData.pcCommercialRetentionRate}
                            onChange={(e) => this.handleNumberChange('pcCommercialRetentionRate', e.target.value)}
                          />
                          {errors.pcCommercialRetentionRate && <div className="mt-1.5 text-sm text-red-600">{errors.pcCommercialRetentionRate}</div>}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Group */}
                {this.state.activeLine === 'group' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-succession-text-primary mb-1">First Year Commissions</label>
                      <div className="flex items-center">
                        <div className="px-4 py-2.5 bg-succession-bg-secondary text-succession-text-muted font-medium border border-r-0 border-succession-border rounded-l-lg">{currencySymbol()}</div>
                        <input
                          type="text"
                          className="w-full px-4 py-2.5 border border-succession-border rounded-r-lg shadow-sm focus:ring-succession-primary focus:border-succession-primary transition-colors bg-succession-bg-primary text-succession-text-primary"
                          value={this.formatCurrency(formData.groupFirstYearCommissions)}
                          onChange={(e) => this.handleCurrencyChange('groupFirstYearCommissions', e.target.value)}
                        />
                      </div>
                      {errors.groupFirstYearCommissions && <div className="mt-1.5 text-sm text-red-600">{errors.groupFirstYearCommissions}</div>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-succession-text-primary mb-1">Renewal Commissions</label>
                      <div className="flex items-center">
                        <div className="px-4 py-2.5 bg-succession-bg-secondary text-succession-text-muted font-medium border border-r-0 border-succession-border rounded-l-lg">{currencySymbol()}</div>
                        <input
                          type="text"
                          className="w-full px-4 py-2.5 border border-succession-border rounded-r-lg shadow-sm focus:ring-succession-primary focus:border-succession-primary transition-colors bg-succession-bg-primary text-succession-text-primary"
                          value={this.formatCurrency(formData.groupRenewalCommissions)}
                          onChange={(e) => this.handleCurrencyChange('groupRenewalCommissions', e.target.value)}
                        />
                      </div>
                      {errors.groupRenewalCommissions && <div className="mt-1.5 text-sm text-red-600">{errors.groupRenewalCommissions}</div>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-succession-text-primary mb-1">Override Commissions</label>
                      <div className="flex items-center">
                        <div className="px-4 py-2.5 bg-succession-bg-secondary text-succession-text-muted font-medium border border-r-0 border-succession-border rounded-l-lg">{currencySymbol()}</div>
                        <input
                          type="text"
                          className="w-full px-4 py-2.5 border border-succession-border rounded-r-lg shadow-sm focus:ring-succession-primary focus:border-succession-primary transition-colors bg-succession-bg-primary text-succession-text-primary"
                          value={this.formatCurrency(formData.groupOverrideCommissions)}
                          onChange={(e) => this.handleCurrencyChange('groupOverrideCommissions', e.target.value)}
                        />
                      </div>
                      {errors.groupOverrideCommissions && <div className="mt-1.5 text-sm text-red-600">{errors.groupOverrideCommissions}</div>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-succession-text-primary mb-1">Other Revenues</label>
                      <div className="flex items-center">
                        <div className="px-4 py-2.5 bg-succession-bg-secondary text-succession-text-muted font-medium border border-r-0 border-succession-border rounded-l-lg">{currencySymbol()}</div>
                        <input
                          type="text"
                          className="w-full px-4 py-2.5 border border-succession-border rounded-r-lg shadow-sm focus:ring-succession-primary focus:border-succession-primary transition-colors bg-succession-bg-primary text-succession-text-primary"
                          value={this.formatCurrency(formData.groupOtherRevenues)}
                          onChange={(e) => this.handleCurrencyChange('groupOtherRevenues', e.target.value)}
                        />
                      </div>
                      {errors.groupOtherRevenues && <div className="mt-1.5 text-sm text-red-600">{errors.groupOtherRevenues}</div>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-succession-text-primary mb-1">Number of Cases</label>
                      <input
                        type="number"
                        className="w-full px-4 py-2.5 border border-succession-border rounded-lg shadow-sm focus:ring-succession-primary focus:border-succession-primary transition-colors bg-succession-bg-primary text-succession-text-primary"
                        value={formData.groupNumberOfCases}
                        onChange={(e) => this.handleNumberChange('groupNumberOfCases', e.target.value, { integer: true })}
                      />
                      {errors.groupNumberOfCases && <div className="mt-1.5 text-sm text-red-600">{errors.groupNumberOfCases}</div>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-succession-text-primary mb-1">Average Case Size</label>
                      <div className="flex items-center">
                        <div className="px-4 py-2.5 bg-succession-bg-secondary text-succession-text-muted font-medium border border-r-0 border-succession-border rounded-l-lg">{currencySymbol()}</div>
                        <input
                          type="text"
                          className="w-full px-4 py-2.5 border border-succession-border rounded-r-lg shadow-sm focus:ring-succession-primary focus:border-succession-primary transition-colors bg-succession-bg-primary text-succession-text-primary"
                          value={this.formatCurrency(formData.groupAverageCaseSize)}
                          onChange={(e) => this.handleCurrencyChange('groupAverageCaseSize', e.target.value)}
                        />
                      </div>
                      {errors.groupAverageCaseSize && <div className="mt-1.5 text-sm text-red-600">{errors.groupAverageCaseSize}</div>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-succession-text-primary mb-1">Case Retention Rate (%)</label>
                      <input
                        type="number"
                        step="0.1"
                        className="w-full px-4 py-2.5 border border-succession-border rounded-lg shadow-sm focus:ring-succession-primary focus:border-succession-primary transition-colors bg-succession-bg-primary text-succession-text-primary"
                        value={formData.groupCaseRetentionRate}
                        onChange={(e) => this.handleNumberChange('groupCaseRetentionRate', e.target.value)}
                      />
                      {errors.groupCaseRetentionRate && <div className="mt-1.5 text-sm text-red-600">{errors.groupCaseRetentionRate}</div>}
                    </div>
                  </div>
                )}
              </div>
            ) : null}

            {/* Wealth/Life Fields (legacy onboarding) */}
            {showWealthLifeFieldsEffective && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Fee Based Revenue */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {this.wealthRevenueLabel('fee.based.revenue', 'fee_based_revenue')}
                  </label>
                  <div className="flex items-center">
                    <div className="px-4 py-2.5 bg-gray-100 text-gray-500 font-medium border border-r-0 border-gray-300 rounded-l-lg">{currencySymbol()}</div>
                    <input
                      type="text"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-r-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                      value={this.formatCurrency(formData.feeRevenue)}
                      onChange={(e) => this.handleCurrencyChange('feeRevenue', e.target.value)}
                    />
                  </div>
                  {errors.feeRevenue && <div className="mt-1.5 text-sm text-red-600 flex items-center"><svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 9a1 1 0 ai-1-1v-4a1 1 0 112 0v4a1 1 0 ai-1 1z" clipRule="evenodd" /></svg>{errors.feeRevenue}</div>}
                </div>
                
                {/* Trails Revenue */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {this.wealthRevenueLabel('trails.revenue', 'trailer_revenue')}
                  </label>
                  <div className="flex items-center">
                    <div className="px-4 py-2.5 bg-gray-100 text-gray-500 font-medium border border-r-0 border-gray-300 rounded-l-lg">{currencySymbol()}</div>
                    <input
                      type="text"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-r-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                      value={this.formatCurrency(formData.trailerRevenue)}
                      onChange={(e) => this.handleCurrencyChange('trailerRevenue', e.target.value)}
                    />
                  </div>
                  {errors.trailerRevenue && <div className="mt-1.5 text-sm text-red-600 flex items-center"><svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 9a1 1 0 ai-1-1v-4a1 1 0 112 0v4a1 1 0 ai-1 1z" clipRule="evenodd" /></svg>{errors.trailerRevenue}</div>}
                </div>
                
                {/* Direct Revenue */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {this.wealthRevenueLabel('direct.revenue', 'direct_revenue')}
                  </label>
                  <div className="flex items-center">
                    <div className="px-4 py-2.5 bg-gray-100 text-gray-500 font-medium border border-r-0 border-gray-300 rounded-l-lg">{currencySymbol()}</div>
                    <input
                      type="text"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-r-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                      value={this.formatCurrency(formData.directRevenue)}
                      onChange={(e) => this.handleCurrencyChange('directRevenue', e.target.value)}
                    />
                  </div>
                  {errors.directRevenue && <div className="mt-1.5 text-sm text-red-600 flex items-center"><svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 9a1 1 0 ai-1-1v-4a1 1 0 112 0v4a1 1 0 ai-1 1z" clipRule="evenodd" /></svg>{errors.directRevenue}</div>}
                </div>
                
                {/* Commission Revenue */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {this.wealthRevenueLabel('commission.revenue', 'transactional_revenue')}
                  </label>
                  <div className="flex items-center">
                    <div className="px-4 py-2.5 bg-gray-100 text-gray-500 font-medium border border-r-0 border-gray-300 rounded-l-lg">{currencySymbol()}</div>
                    <input
                      type="text"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-r-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                      value={this.formatCurrency(formData.commissionRevenue)}
                      onChange={(e) => this.handleCurrencyChange('commissionRevenue', e.target.value)}
                    />
                  </div>
                  {errors.commissionRevenue && <div className="mt-1.5 text-sm text-red-600 flex items-center"><svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 9a1 1 0 ai-1-1v-4a1 1 0 112 0v4a1 1 0 ai-1 1z" clipRule="evenodd" /></svg>{errors.commissionRevenue}</div>}
                </div>
              </div>
            )}
            
            {/* P&C Fields */}
            {showPCFieldsEffective && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {businessType === 'p_and_c' ? (
                  <>
                    {/* Annual Revenue */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {this.getTranslatedText('recurring.revenue')}
                      </label>
                      <div className="flex items-center">
                        <div className="px-4 py-2.5 bg-gray-100 text-gray-500 font-medium border border-r-0 border-gray-300 rounded-l-lg">{currencySymbol()}</div>
                        <input
                          type="text"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-r-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                          value={this.formatCurrency(formData.annualRevenue)}
                          onChange={(e) => this.handleCurrencyChange('annualRevenue', e.target.value)}
                        />
                      </div>
                      {errors.annualRevenue && <div className="mt-1.5 text-sm text-red-600 flex items-center"><svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 9a1 1 0 ai-1-1v-4a1 1 0 112 0v4a1 1 0 ai-1 1z" clipRule="evenodd" /></svg>{errors.annualRevenue}</div>}
                    </div>
                    
                    {/* Recurring Revenue */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {this.getTranslatedText('non.recurring.revenue')}
                      </label>
                      <div className="flex items-center">
                        <div className="px-4 py-2.5 bg-gray-100 text-gray-500 font-medium border border-r-0 border-gray-300 rounded-l-lg">{currencySymbol()}</div>
                        <input
                          type="text"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-r-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                          value={this.formatCurrency(formData.recurringRevenue)}
                          onChange={(e) => this.handleCurrencyChange('recurringRevenue', e.target.value)}
                        />
                      </div>
                      {errors.recurringRevenue && <div className="mt-1.5 text-sm text-red-600 flex items-center"><svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 9a1 1 0 ai-1-1v-4a1 1 0 112 0v4a1 1 0 ai-1 1z" clipRule="evenodd" /></svg>{errors.recurringRevenue}</div>}
                    </div>
                  </>
                ) : (
                  <>
                    {/* Premium */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {this.getTranslatedText('premium')}
                      </label>
                      <div className="flex items-center">
                        <div className="px-4 py-2.5 bg-gray-100 text-gray-500 font-medium border border-r-0 border-gray-300 rounded-l-lg">{currencySymbol()}</div>
                        <input
                          type="text"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-r-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                          value={this.formatCurrency(formData.premium)}
                          onChange={(e) => this.handleCurrencyChange('premium', e.target.value)}
                        />
                      </div>
                      {errors.premium && <div className="mt-1.5 text-sm text-red-600 flex items-center"><svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 9a1 1 0 ai-1-1v-4a1 1 0 112 0v4a1 1 0 ai-1 1z" clipRule="evenodd" /></svg>{errors.premium}</div>}
                    </div>
                    
                    {/* Recurring Revenue */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {this.getTranslatedText('recurring.revenue')}
                      </label>
                      <div className="flex items-center">
                        <div className="px-4 py-2.5 bg-gray-100 text-gray-500 font-medium border border-r-0 border-gray-300 rounded-l-lg">{currencySymbol()}</div>
                        <input
                          type="text"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-r-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                          value={this.formatCurrency(formData.recurringRevenue)}
                          onChange={(e) => this.handleCurrencyChange('recurringRevenue', e.target.value)}
                        />
                      </div>
                      {errors.recurringRevenue && <div className="mt-1.5 text-sm text-red-600 flex items-center"><svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 9a1 1 0 ai-1-1v-4a1 1 0 112 0v4a1 1 0 ai-1 1z" clipRule="evenodd" /></svg>{errors.recurringRevenue}</div>}
                    </div>
                    
                    {/* Non-Recurring Revenue */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {this.getTranslatedText('non.recurring.revenue')}
                      </label>
                      <div className="flex items-center">
                        <div className="px-4 py-2.5 bg-gray-100 text-gray-500 font-medium border border-r-0 border-gray-300 rounded-l-lg">{currencySymbol()}</div>
                        <input
                          type="text"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-r-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                          value={this.formatCurrency(formData.nonRecurringRevenue)}
                          onChange={(e) => this.handleCurrencyChange('nonRecurringRevenue', e.target.value)}
                        />
                      </div>
                      {errors.nonRecurringRevenue && <div className="mt-1.5 text-sm text-red-600 flex items-center"><svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 9a1 1 0 ai-1-1v-4a1 1 0 112 0v4a1 1 0 ai-1 1z" clipRule="evenodd" /></svg>{errors.nonRecurringRevenue}</div>}
                    </div>
                  </>
                )}
              </div>
            )}
            
            {/* Default Revenue Section - shown when neither Wealth/Life nor P&C is enabled */}
            {(!showWealthLifeFieldsEffective && !showPCFieldsEffective && !isMultiLine) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Fee Revenue */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {this.getTranslatedText('fee.revenue')}
                  </label>
                  <div className="flex items-center">
                    <div className="px-4 py-2.5 bg-gray-100 text-gray-500 font-medium border border-r-0 border-gray-300 rounded-l-lg">{currencySymbol()}</div>
                    <input
                      type="text"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-r-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                      value={this.formatCurrency(formData.feeRevenue)}
                      onChange={(e) => this.handleCurrencyChange('feeRevenue', e.target.value)}
                    />
                  </div>
                  {errors.feeRevenue && <div className="mt-1.5 text-sm text-red-600 flex items-center"><svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 9a1 1 0 ai-1-1v-4a1 1 0 112 0v4a1 1 0 ai-1 1z" clipRule="evenodd" /></svg>{errors.feeRevenue}</div>}
                </div>
                
                {/* Commission Revenue */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {this.getTranslatedText('commission.revenue')}
                  </label>
                  <div className="flex items-center">
                    <div className="px-4 py-2.5 bg-gray-100 text-gray-500 font-medium border border-r-0 border-gray-300 rounded-l-lg">{currencySymbol()}</div>
                    <input
                      type="text"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-r-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                      value={this.formatCurrency(formData.commissionRevenue)}
                      onChange={(e) => this.handleCurrencyChange('commissionRevenue', e.target.value)}
                    />
                  </div>
                  {errors.commissionRevenue && <div className="mt-1.5 text-sm text-red-600 flex items-center"><svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 9a1 1 0 ai-1-1v-4a1 1 0 112 0v4a1 1 0 ai-1 1z" clipRule="evenodd" /></svg>{errors.commissionRevenue}</div>}
                </div>
              </div>
            )}
            
            {/* Total Production */}
            <div className="mt-6 pt-4 border-t border-succession-border">
              <div className="flex justify-between items-center">
                <span className="text-succession-text-primary font-medium">{this.getTranslatedText('total.production')}</span>
                <span className="text-succession-primary font-bold text-lg bg-succession-primary-10 py-1 px-4 rounded-full"
                      style={{ backgroundColor: 'var(--succession-primary-10, rgba(84, 137, 182, 0.1))', color: 'var(--succession-primary, #5489B6)' }}>
                  {currencySymbol()}{this.formatCurrency(
                    isMultiLine
                      ? this.calculateMultiLineTotal()
                      : (showWealthLifeFieldsEffective 
                        ? this.calculateWealthTotal()
                        : (showPCFieldsEffective 
                          ? (businessType === 'p_and_c' 
                            ? Number(formData.annualRevenue || 0) + Number(formData.recurringRevenue || 0) 
                            : this.calculatePCTotal())
                          : this.calculateDefaultTotal()))
                  )}
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end items-center mt-8 pt-6 border-t border-succession-border">
            {/* Back button hidden - users cannot go back to contact information step */}
            <button 
              type="submit" 
              className={`inline-flex items-center py-2.5 px-6 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white transition-colors ${isSubmitting || loading ? 'bg-succession-primary opacity-60 cursor-not-allowed' : 'bg-succession-primary hover:bg-succession-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-succession-primary hover:-translate-y-0.5 transition-transform'}`}
              disabled={isSubmitting || loading}
            >
              {isSubmitting || loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {this.getTranslatedText('saving')}
                </>
              ) : (
                <>
                  {this.getTranslatedText('continue')}
                  <svg className="ml-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    );
  }
}

ProductionInformationStep.propTypes = {
  user: PropTypes.object,
  onNext: PropTypes.func,
  onUserUpdate: PropTypes.func,
  mutate: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  onBack: PropTypes.func,
  currentLanguage: PropTypes.string,
  onboardingContext: PropTypes.object,
  // Supplied by OnboardingWizard from its getCurrentUser query's company.
  companyRevenueSettings: PropTypes.shape({
    loading: PropTypes.bool,
    company: PropTypes.shape({
      max_revenue_input: PropTypes.number,
      revenue_line_terminology: PropTypes.object,
    }),
  }),
};

// Wrap component with the GraphQL mutation
export default graphql(UPDATE_CURRENT_USER, {
  options: { 
    errorPolicy: 'all' 
  }
})(withOnboardingContext(ProductionInformationStep)); 